import React, { useEffect, useState, useMemo, useRef } from 'react';
import { Circle, OverlayView } from '@react-google-maps/api';
import { Paper, Box, Typography, IconButton, Avatar } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

// Inject CSS for pulse animation (once)
if (typeof document !== 'undefined') {
  const styleId = 'marker-pulse-style';
  if (!document.getElementById(styleId)) {
    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = `
      @keyframes marker-pulse {
        0% { transform: scale(1); }
        50% { transform: scale(1.2); }
        100% { transform: scale(1); }
      }
      .marker-pulsing {
        animation: marker-pulse 0.6s ease-out;
      }
    `;
    document.head.appendChild(style);
  }
}

// Distinct color palette for multiple people
const PERSON_COLORS = [
  '#1565c0', // Deep blue
  '#b71c1c', // Deep crimson
  '#2e7d32', // Forest green
  '#bf8040', // Muted bronze
  '#6a1b9a', // Deep purple
  '#00838f', // Deep cyan
  '#f57f17', // Deep amber
  '#c2185b', // Deep magenta
  '#4e342e', // Dark brown
  '#455a64', // Dark blue grey
];

// Generate color for a person based on their name
const getPersonColor = (personName, personMap) => {
  if (!personName) return '#1565c0';

  // If we have a person map (multiple people), use indexed colors
  if (personMap && personMap.size > 1) {
    const index = personMap.get(personName);
    return PERSON_COLORS[index % PERSON_COLORS.length];
  }

  // Single person, use default blue
  return '#1565c0';
};

/**
 * MapMarkers - Render markers and info windows for events using AdvancedMarkerElement
 */
function MapMarkers({
  map,
  events,
  personName,
  showAccuracy,
  addresses,
  loadingAddresses,
  selectedIndex,
  onMarkerClick
}) {
  const [openInfoWindowIndex, setOpenInfoWindowIndex] = useState(null);
  const [infoWindowAnchor, setInfoWindowAnchor] = useState(null);
  const markersRef = useRef([]);
  const isMultiple = events.length > 1;

  // Build person name map for color coding
  const personMap = useMemo(() => {
    const uniquePersons = new Map();
    events.forEach(event => {
      const rawName = event.metadata?.personName || event.userId || 'Unknown';
      const eventPersonName = rawName.trim(); // Normalize whitespace
      if (!uniquePersons.has(eventPersonName)) {
        uniquePersons.set(eventPersonName, uniquePersons.size);
      }
    });
    return uniquePersons;
  }, [events]);

  // Open info window by default for single event
  useEffect(() => {
    if (!isMultiple && events.length > 0) {
      setOpenInfoWindowIndex(0);
      if (markersRef.current[0]) {
        setInfoWindowAnchor(markersRef.current[0]);
      }
    }
  }, [isMultiple, events.length]);

  // Create and manage AdvancedMarkerElements
  useEffect(() => {
    if (!map || !window.google?.maps?.marker?.AdvancedMarkerElement) return;

    // Clear old markers
    markersRef.current.forEach(marker => {
      if (marker.map) marker.map = null;
    });
    markersRef.current = [];

    // Track positions to detect duplicates and offset them
    const positionMap = new Map();

    // Create new markers
    events.forEach((event, index) => {
      const { latitude, longitude } = event.location;
      let lat = latitude;
      let lng = longitude;

      // Check if this exact position already exists
      const posKey = `${latitude.toFixed(6)},${longitude.toFixed(6)}`;
      if (positionMap.has(posKey)) {
        // Offset by a small amount (~0.5 meters at equator = ~0.000005 degrees)
        const offsetCount = positionMap.get(posKey);
        const angle = (offsetCount * 60) * (Math.PI / 180); // 60 degrees apart
        const offsetAmount = 0.000005;
        lat += offsetAmount * Math.cos(angle);
        lng += offsetAmount * Math.sin(angle);
        positionMap.set(posKey, offsetCount + 1);
      } else {
        positionMap.set(posKey, 1);
      }

      const position = { lat, lng };
      const rawName = event.metadata?.personName || event.userId || 'Unknown';
      const eventPersonName = rawName.trim();
      const markerColor = getPersonColor(eventPersonName, personMap);

      // Create custom pin element
      const pinElement = new window.google.maps.marker.PinElement({
        background: markerColor,
        borderColor: 'white',
        glyphColor: 'white',
        glyph: isMultiple ? `${index + 1}` : '',
        scale: isMultiple ? 0.9 : 1.0,
      });

      // Create advanced marker - use PinElement directly, not .element
      const marker = new window.google.maps.marker.AdvancedMarkerElement({
        map,
        position,
        content: pinElement,
        title: eventPersonName,
      });

      // Add click listener using gmp-click event
      marker.addListener('gmp-click', () => {
        setOpenInfoWindowIndex(index);
        setInfoWindowAnchor(marker);
        // Trigger sidebar focus
        if (onMarkerClick) {
          onMarkerClick(index);
        }
      });

      markersRef.current[index] = marker;
    });

    // Cleanup on unmount
    return () => {
      markersRef.current.forEach(marker => {
        if (marker.map) marker.map = null;
      });
      markersRef.current = [];
    };
  }, [map, events, personMap, isMultiple]);

  // Handle bounce animation and open info window for selected marker
  useEffect(() => {
    if (selectedIndex !== null && markersRef.current[selectedIndex]) {
      const marker = markersRef.current[selectedIndex];

      // Open/move the info window to clicked location
      // The key prop on InfoWindow ensures it remounts when index changes
      setOpenInfoWindowIndex(selectedIndex);
      setInfoWindowAnchor(marker);

      // Pan map only if marker or info window is outside visible area
      if (map) {
        const event = events[selectedIndex];
        const { latitude, longitude } = event.location;
        const position = { lat: latitude, lng: longitude };

        // Check if marker is visible
        const bounds = map.getBounds();
        if (!bounds || !bounds.contains(position)) {
          // Marker is outside viewport, pan to it
          map.panTo(position);
          return;
        }

        // Marker is visible, check if info window would be cut off at top
        const projection = map.getProjection();
        const mapDiv = map.getDiv();

        if (projection && mapDiv) {
          const scale = Math.pow(2, map.getZoom());
          const worldCoordinate = projection.fromLatLngToPoint(position);
          const centerPoint = projection.fromLatLngToPoint(map.getCenter());

          // Convert to pixel coordinates
          const markerPixel = new window.google.maps.Point(
            worldCoordinate.x * scale,
            worldCoordinate.y * scale
          );
          const centerPixel = new window.google.maps.Point(
            centerPoint.x * scale,
            centerPoint.y * scale
          );

          // Info window is 200px above marker + ~150px height = 350px total
          const INFO_WINDOW_TOP_OFFSET = 350;
          const mapHeight = mapDiv.clientHeight;

          // Calculate where info window top would be relative to viewport
          const markerOffsetY = markerPixel.y - centerPixel.y; // Relative to center
          const infoWindowTopY = markerOffsetY - INFO_WINDOW_TOP_OFFSET;

          // Top edge of viewport (negative y values)
          const viewportTop = -mapHeight / 2;

          // Only pan if info window would be cut off at top (with 50px margin)
          if (infoWindowTopY < viewportTop + 50) {
            const panAmount = (viewportTop + 50) - infoWindowTopY;

            const newCenterPixel = new window.google.maps.Point(
              centerPixel.x,
              centerPixel.y - panAmount
            );

            const newCenterWorld = new window.google.maps.Point(
              newCenterPixel.x / scale,
              newCenterPixel.y / scale
            );

            const newCenter = projection.fromPointToLatLng(newCenterWorld);
            map.panTo(newCenter);
          }
        }
      }

      // Bounce animation - PinElement is now the content directly
      const pinElement = marker.content;
      if (pinElement?.element) {
        // Access the actual DOM element from PinElement
        pinElement.element.classList.add('marker-pulsing');
        setTimeout(() => {
          pinElement.element.classList.remove('marker-pulsing');
        }, 600);
      }
    }
  }, [selectedIndex, map, events]);


  return (
    <>
      {/* Custom Info Panel - doesn't steal focus */}
      {openInfoWindowIndex !== null && (() => {
        const event = events[openInfoWindowIndex];
        const { latitude, longitude } = event.location;
        const addressKey = `${latitude},${longitude}`;
        const address = addresses[addressKey];
        const rawName = event.metadata?.personName || event.userId || 'Unknown';
        const eventPersonName = rawName.trim();
        const markerColor = getPersonColor(eventPersonName, personMap);
        const personImage = event.metadata?.personImage;

        return (
          <OverlayView
            position={{ lat: latitude, lng: longitude }}
            mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
            getPixelPositionOffset={(width, height) => ({
              x: -(width / 2),
              y: -(height + 200) // Position far above marker
            })}
          >
            <Paper
              elevation={6}
              sx={{
                minWidth: 208,
                maxWidth: 234,
                p: 0.75,
                pointerEvents: 'auto',
                bgcolor: 'background.paper',
                border: 1.5,
                borderColor: markerColor
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, mb: 0.5 }}>
                {personImage && (
                  <Avatar
                    src={personImage}
                    alt={eventPersonName}
                    sx={{ width: 24, height: 24, flexShrink: 0 }}
                  />
                )}
                <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 600,
                      color: markerColor,
                      lineHeight: 1.1,
                      wordBreak: 'break-word',
                      fontSize: '0.7rem',
                      display: 'block'
                    }}
                  >
                    {eventPersonName}
                  </Typography>
                  {isMultiple && (
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 600,
                        display: 'block',
                        color: 'text.secondary',
                        fontSize: '0.6rem'
                      }}
                    >
                      {openInfoWindowIndex + 1}/{events.length}
                    </Typography>
                  )}
                </Box>
                <IconButton
                  size="small"
                  onClick={() => {
                    setOpenInfoWindowIndex(null);
                    setInfoWindowAnchor(null);
                  }}
                  sx={{ p: 0, flexShrink: 0, mt: -0.5, mr: -0.5 }}
                >
                  <CloseIcon sx={{ fontSize: 12 }} />
                </IconButton>
              </Box>

              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  mb: 0.25,
                  fontSize: '0.6rem'
                }}
              >
                {new Date(event.timestamp || event.createdAt).toLocaleString([], {
                  month: 'short',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </Typography>

              {address && (
                <Typography
                  variant="caption"
                  sx={{
                    fontStyle: 'italic',
                    display: 'block',
                    mb: 0.25,
                    fontSize: '0.6rem',
                    wordBreak: 'break-word'
                  }}
                >
                  {isMultiple ? address.short : address.formatted}
                </Typography>
              )}

              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                  fontSize: '0.55rem',
                  display: 'block',
                  fontFamily: 'monospace'
                }}
              >
                {latitude.toFixed(4)}, {longitude.toFixed(4)}
              </Typography>
            </Paper>
          </OverlayView>
        );
      })()}

      {/* Accuracy circles */}
      {showAccuracy && events.map((event, index) => {
        if (!event.location?.accuracy) return null;
        const { latitude, longitude, accuracy } = event.location;
        const position = { lat: latitude, lng: longitude };
        const rawName = event.metadata?.personName || event.userId || 'Unknown';
        const eventPersonName = rawName.trim();
        const markerColor = getPersonColor(eventPersonName, personMap);

        // Display accuracy circle at 50% of GPS accuracy radius for visual clarity
        const ACCURACY_RADIUS_MULTIPLIER = 0.5;

        return (
          <Circle
            key={`circle-${event.eventId || index}`}
            center={position}
            radius={accuracy * ACCURACY_RADIUS_MULTIPLIER}
            options={{
              strokeColor: markerColor,
              strokeOpacity: 0.8,
              strokeWeight: 1,
              fillColor: markerColor,
              fillOpacity: 0.1,
            }}
          />
        );
      })}
    </>
  );
}

// Memoize to prevent expensive marker re-renders
export default React.memo(MapMarkers);
